import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import RedeemCodeBox from "../components/promo/RedeemCodeBox";
import { useLazyGetBonusDetailsQuery } from "../features/api/bonusApiSlice";
import { useGetWhatsappGroupQuery } from "../features/api/publicApiSlice";

/**
 * The bonus row that carries this page's banner and marketing copy. It is the same
 * container row every redemption award books against, and it is toDisplay=false so it
 * never appears in the Promo Center grid - but /bonusDetails resolves by bonus_code with
 * no toDisplay filter, so it can still be fetched here.
 */
const CONTAINER_BONUS_CODE = "redemption_code";

const RedemptionCode = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const theme = useTheme();

    // Same breakpoint Layout uses to swap in MobileHeader (Layout.js:51). Below it the
    // header bar already renders the page title, so the in-page heading is a duplicate.
    const mobileView = useMediaQuery(theme.breakpoints.down("sm"));

    // Standalone (mobile route) vs embedded in ProfileWallet - drives only the top/bottom
    // spacing needed to clear the fixed header. Unlike PromoCenter this page needs no user
    // object: the code box authenticates via the stored token and everything it renders
    // comes from the bonus lookup.
    const [isNotProfileWallet] = useState(
        location?.state?.isNotProfileWallet || false
    );

    const redirectMobile = useRef(null);

    const [getBonusDetails, { data: bonusDetails }] = useLazyGetBonusDetailsQuery({ prefetch: true });

    // Config-backed chat-group link for the Join Now call to action.
    const { data: whatsappGroup } = useGetWhatsappGroupQuery();
    const whatsappGroupUrl = whatsappGroup?.result?.value;

    useEffect(() => {
        getBonusDetails(CONTAINER_BONUS_CODE);
    }, []);

    const details = bonusDetails?.result;
    const bannerUrl = details?.bonusTypeThumbnailUrl || details?.thumbnailUrl;
    const content = details?.content;
    const heading = details?.displayTitle || t("promocenter.redeempagetitle");

    const textColor =
        process.env.REACT_APP_COLOR_WALLET_TEXT || process.env.REACT_APP_COLOR_TEXT || "white";

    return (
        <Box>
            <Box
                ref={redirectMobile}
                sx={{
                    marginTop: isNotProfileWallet ? 10 : undefined,
                    marginBottom: isNotProfileWallet ? 15 : undefined,
                    color: textColor,
                    paddingX: { xs: 1.5, sm: 3 },
                }}
            >
                {!mobileView && (
                    <Typography fontWeight="bold" color={textColor}>
                        {heading}
                    </Typography>
                )}

                <RedeemCodeBox />

                {/* Banner and copy come from the container bonus row. Both are hidden when
                    empty so an unpopulated row degrades to just the code box rather than a
                    broken image or a blank slab. */}
                {bannerUrl && (
                    <Box
                        sx={{
                            position: "relative",
                            width: "100%",
                            paddingTop: "31.25%",
                            borderRadius: "8px",
                            overflow: "hidden",
                            mt: 3,
                        }}
                    >
                        <Box
                            component="img"
                            src={bannerUrl}
                            alt={heading}
                            sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                    </Box>
                )}

                {/* Chat-group CTA, between the banner and the copy. Opens the WHATSAPP_GROUP
                    link when a value is configured, otherwise falls back to live chat. */}
                <Box
                    sx={{
                        mt: 2.5,
                        mb: 2.5,
                        display: "flex",
                        // Centred on mobile, left-aligned on desktop.
                        justifyContent: { xs: "center", sm: "flex-start" },
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={() => {
                            // if value is "livechat" opens live chat, empty/null does nothing, anything else opens it as a URL.
                            const value = whatsappGroupUrl?.trim();
                            if (value?.toLowerCase() === "livechat") {
                                if (
                                    typeof window.LiveChatWidget !== "undefined" &&
                                    window.LiveChatWidget.call
                                ) {
                                    window.LiveChatWidget.call("maximize");
                                }
                            } else if (value) {
                                window.open(value, "_blank", "noopener,noreferrer");
                            }
                        }}
                        sx={{
                            background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                            borderRadius: "14px",
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: 15,
                            letterSpacing: 0.3,
                            color: "white",
                            px: 3.5,
                            py: 1.15,
                            // Comfortable width on mobile, hug-content on desktop.
                            width: { xs: "100%", sm: "auto" },
                            maxWidth: { xs: 320, sm: "none" },
                            boxShadow: "0 6px 16px rgba(37, 211, 102, 0.35)",
                            transition: "transform 0.15s ease, box-shadow 0.15s ease",
                            "&:hover": {
                                background: "linear-gradient(135deg, #25D366 0%, #0e7566 100%)",
                                boxShadow: "0 8px 22px rgba(37, 211, 102, 0.45)",
                                transform: "translateY(-2px)",
                            },
                        }}
                    >
                        {t("promocenter.chatwithus") !== "promocenter.chatwithus" ? t("promocenter.chatwithus") : "Chat with us"}
                    </Button>
                </Box>

                {content && (
                    // The stored HTML is authored in the backoffice and carries its own
                    // inline table styling. On a full-width page (unlike the promo modal)
                    // a wide table can overflow the viewport, hence the scroll container
                    // and the table/img rules.
                    <Box sx={{ mt: 2, overflowX: "auto" }}>
                        <Box
                            sx={{
                                fontSize: 13,
                                color: textColor,
                                lineHeight: 1.6,
                                "& p": { mt: 0, mb: 0.75 },
                                "& img": { maxWidth: "100%", height: "auto" },
                                "& table": { width: "100%", borderCollapse: "collapse" },
                            }}
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default RedemptionCode;
